import { useEffect, useRef } from "react";

export const BALANCE_REFRESH_INTERVAL_MS = 15_000;

/**
 * Запускает фоновое обновление только пока Mini App видим.
 *
 * Следующий таймер ставится после завершения текущего запроса: медленный API не
 * создаёт очередь параллельных refresh. Возврат во вкладку и focus обновляют
 * данные сразу, а cleanup запрещает повторное планирование после unmount.
 */
export function useVisibleRefresh(
  refresh: () => Promise<unknown>,
  intervalMs = BALANCE_REFRESH_INTERVAL_MS,
) {
  const refreshRef = useRef(refresh);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    let active = true;
    let timerId: number | undefined;
    let refreshInFlight: Promise<unknown> | null = null;

    function clearTimer() {
      if (timerId === undefined) return;
      window.clearTimeout(timerId);
      timerId = undefined;
    }

    function schedule() {
      if (
        !active ||
        document.hidden ||
        timerId !== undefined ||
        refreshInFlight !== null
      ) {
        return;
      }
      timerId = window.setTimeout(() => {
        timerId = undefined;
        runNow();
      }, intervalMs);
    }

    function finish(request: Promise<unknown>) {
      if (refreshInFlight === request) refreshInFlight = null;
      if (active) schedule();
    }

    function runNow() {
      if (!active || document.hidden || refreshInFlight !== null) return;
      clearTimer();
      const request = Promise.resolve().then(() => refreshRef.current());
      refreshInFlight = request;
      void request.then(
        () => finish(request),
        () => finish(request),
      );
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        clearTimer();
        return;
      }
      runNow();
    }

    function handleFocus() {
      if (!document.hidden) runNow();
    }

    schedule();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    return () => {
      active = false;
      clearTimer();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [intervalMs]);
}
